import { z } from 'zod';

/**
 * Metric Hub user onboarding progress — persisted in
 * `users/{email}/onboarding/state` so progress survives device + browser
 * changes. Mirrored to localStorage by the frontend hook for instant
 * first-paint without waiting on the Firestore read.
 *
 * Drives:
 *  - The "Start Here" hero, training tracks, tour catalog, and badges
 *    wall.
 *  - The 12 mini-tours' catalog + their "Take all" chained variant.
 *  - Auto-skill tracking when the user visits surfaces or completes
 *    key actions.
 *  - The first-visit auto-redirect (no firstSeenAt → stamp + redirect).
 *  - Server-derived streak + badges (single source of truth).
 *
 * Schema versions:
 *  - v1 (pre-2026-05-29) — localStorage-only, single bit per checklist item.
 *  - v2 (2026-05-29) — Firestore-backed, `completed[]` + single `tour:`.
 *  - v3 (this file) — `skills[]` + `tourState` + `tourCompletions{}` +
 *    `streak` + `badges[]`. Legacy v2 docs are migrated lazily on the
 *    first GET (see `apps/api/src/routes/metric-hub/onboarding.ts`).
 */

const isoDateString = z.string().datetime();

export const onboardingRoleSchema = z.enum(['creator', 'reviewer', 'consumer']);
export type OnboardingRole = z.infer<typeof onboardingRoleSchema>;

/**
 * Unique IDs for each of the 12 mini-tours that together cover 100% of
 * the Metric Hub surfaces. Each tour is opt-in (max 5 steps to keep
 * 2026 completion rates high). The "Take all" button chains them in
 * an order tailored to the user's role.
 */
export const onboardingTourIdSchema = z.enum([
  'start_here',
  'dashboard',
  'catalog',
  'semantic_models',
  'graph',
  'glossary',
  'docs',
  'propose_metric',
  'propose_model',
  'inbox',
  'my_drafts',
  'sync_dashboard',
]);
export type OnboardingTourId = z.infer<typeof onboardingTourIdSchema>;

/**
 * Audience + gating for each tour — the SINGLE source of truth for tour
 * VISIBILITY, shared by the frontend catalog filter (`getToursForUser`) and
 * the backend tour-requirement count (`expectedTourCount`) so the two can
 * never drift. They drifted before: the server over-counted (it treated
 * `my_drafts` as a consumer tour and mis-gated `inbox`), which made the
 * training certificate's "all role tours" requirement unsatisfiable for some
 * roles. Keep audiences here in lockstep with the catalog's display metadata.
 */
export const onboardingTourVisibility: Record<
  OnboardingTourId,
  { audience: OnboardingRole[]; gated?: 'reviewer' | 'admin' }
> = {
  start_here: { audience: ['creator', 'reviewer', 'consumer'] },
  dashboard: { audience: ['creator', 'reviewer', 'consumer'] },
  catalog: { audience: ['creator', 'reviewer', 'consumer'] },
  semantic_models: { audience: ['creator', 'reviewer'] },
  graph: { audience: ['creator', 'reviewer', 'consumer'] },
  glossary: { audience: ['creator', 'reviewer', 'consumer'] },
  docs: { audience: ['creator', 'reviewer', 'consumer'] },
  propose_metric: { audience: ['creator', 'reviewer'] },
  propose_model: { audience: ['creator', 'reviewer'] },
  inbox: { audience: ['reviewer'], gated: 'reviewer' },
  my_drafts: { audience: ['creator', 'reviewer'] },
  sync_dashboard: { audience: ['creator', 'reviewer', 'consumer'], gated: 'admin' },
};

/**
 * Tour ids visible to a user given their onboarding role + privileges.
 * Identical predicate on both ends (the frontend filters the catalog by this,
 * the backend counts it). `isReviewer`/`isAdmin` use the canonical reviewer
 * signal (admin, or DATA-area with the reviewer flag) — see GET /auth/me.
 */
export function visibleTourIds(
  role: OnboardingRole | null,
  isReviewer: boolean,
  isAdmin: boolean,
): OnboardingTourId[] {
  return (Object.keys(onboardingTourVisibility) as OnboardingTourId[]).filter((id) => {
    const v = onboardingTourVisibility[id];
    if (v.gated === 'admin' && !isAdmin) return false;
    if (v.gated === 'reviewer' && !isReviewer && !isAdmin) return false;
    if (role && !v.audience.includes(role)) return false;
    return true;
  });
}

/** Number of tours that count toward the "completed all role tours" milestone. */
export function expectedTourCount(
  role: OnboardingRole | null,
  isReviewer: boolean,
  isAdmin: boolean,
): number {
  return visibleTourIds(role, isReviewer, isAdmin).length;
}

/**
 * Granular learning objectives the user can master. Each skill is
 * either unlocked by a tour completion (mapped via `TourDef.skillUnlocked`)
 * or by performing the corresponding action on the actual surface.
 * Server unions on PATCH, never replaces.
 */
export const onboardingSkillSchema = z.enum([
  // v2 carry-overs (same string values so legacy `completed[]` migrates).
  'find_metric',
  'ask_atlas',
  'save_metric',
  'propose_metric',
  'invite_teammate',
  // v3 additions covering the new surfaces + actions.
  'propose_model',
  'review_draft',
  'open_graph',
  'add_glossary_term',
  'read_docs',
  'view_dashboard',
  'check_inbox',
  'view_my_drafts',
  'monitor_sync',
  'finish_tour_core',
]);
export type OnboardingSkill = z.infer<typeof onboardingSkillSchema>;

/** Deprecated v2 alias — kept so existing UI code compiles during the
 *  migration. New code should import `onboardingSkillSchema`. */
export const onboardingChecklistItemSchema = onboardingSkillSchema;
export type OnboardingChecklistItem = OnboardingSkill;

export const onboardingTourStateSchema = z.object({
  /** Zero-based index of the step the user is currently on. */
  currentStepIdx: z.number().int().nonnegative(),
  /** Timestamp of the last step transition. The frontend's "resume?"
   *  prompt only fires when this is < 24h ago to avoid stale offers. */
  lastInteractionAt: isoDateString,
  /** Stamp set when the tour reached its final step OR the user clicked
   *  "Finish". `null` means the tour is in flight or never started. */
  finishedAt: isoDateString.nullable().default(null),
});
export type OnboardingTourState = z.infer<typeof onboardingTourStateSchema>;

/** Per-tour completion record. Keys of the parent map are tour IDs. */
export const tourCompletionSchema = z.object({
  completedAt: isoDateString,
  /** Wall-clock ms between `startTour` and `STATUS.FINISHED`. Used by
   *  the catalog to show "you took X min last time" hints. */
  durationMs: z.number().int().nonnegative(),
  /** How many steps the tour had at the time of completion. Lets us
   *  detect "this tour grew since you last took it" cases. */
  stepCount: z.number().int().positive(),
});
export type TourCompletion = z.infer<typeof tourCompletionSchema>;

/** Consecutive days where the user advanced at least one skill or
 *  finished a tour. Reset to 1 if a day was skipped. Server-derived. */
export const onboardingStreakSchema = z.object({
  count: z.number().int().nonnegative().default(0),
  lastActionAt: isoDateString.nullable().default(null),
});
export type OnboardingStreak = z.infer<typeof onboardingStreakSchema>;

/**
 * 8 milestone badges. Server adds them to the array when criteria are
 * met during a PATCH (single source of truth — client never computes).
 *
 *  - `first_steps`    — 3 skills mastered
 *  - `half_way`       — 50% of role's skills mastered
 *  - `master`         — 100% of role's skills mastered
 *  - `tour_explorer`  — 3 tours completed
 *  - `tour_champion`  — all available tours for the user's role done
 *  - `streak_3` / `streak_7` / `streak_30` — N-day streaks
 */
export const onboardingBadgeIdSchema = z.enum([
  'first_steps',
  'half_way',
  'master',
  'tour_explorer',
  'tour_champion',
  'streak_3',
  'streak_7',
  'streak_30',
]);
export type OnboardingBadgeId = z.infer<typeof onboardingBadgeIdSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Training certificate — a user earns a certificate after completing four
// requirements: (1) all role-relevant tours, (2) watching the onboarding
// video to ~100% coverage, (3) reading the docs (scroll to end + active time),
// (4) passing a 5-question test. The `training` block below lives on the same
// onboarding doc. Client-reported fields are monotonic (never regress);
// `test` + `certificate` are server-authoritative (the client cannot set them).
// ──────────────────────────────────────────────────────────────────────────

/** One graded test attempt — bounded ring buffer on the test record. */
export const trainingTestAttemptSchema = z.object({
  at: isoDateString,
  score: z.number().int().min(0).max(5),
  seed: z.string(),
});
export type TrainingTestAttempt = z.infer<typeof trainingTestAttemptSchema>;

/** Server-authoritative test summary. The client never sets these. */
export const trainingTestSchema = z.object({
  attempts: z.number().int().nonnegative().default(0),
  bestScore: z.number().int().min(0).max(5).default(0),
  lastAttemptAt: isoDateString.nullable().default(null),
  /** Stamped server-side the first time bestScore reaches the pass mark. */
  passedAt: isoDateString.nullable().default(null),
  recentAttempts: z.array(trainingTestAttemptSchema).max(20).default([]),
});
export type TrainingTest = z.infer<typeof trainingTestSchema>;

/** Server-only certificate stamp. */
export const trainingCertificateSchema = z.object({
  issuedAt: isoDateString.nullable().default(null),
  serial: z.string().nullable().default(null),
});
export type TrainingCertificate = z.infer<typeof trainingCertificateSchema>;

/** Admin "remind to finish training" bookkeeping (server/admin-written only).
 *  Drives the dedupe cooldown so a user isn't re-nudged within N days. */
export const trainingNudgeSchema = z.object({
  lastNudgedAt: isoDateString.nullable().default(null),
  count: z.number().int().nonnegative().default(0),
  lastNudgedBy: z.string().nullable().default(null),
});
export type TrainingNudge = z.infer<typeof trainingNudgeSchema>;

export const trainingStateSchema = z.object({
  /** Best video coverage seen (0..100). Monotonic max on the server. */
  videoWatchedPct: z.number().min(0).max(100).default(0),
  /** Server-derived: videoWatchedPct >= VIDEO_COVERAGE_THRESHOLD. */
  videoCoverageComplete: z.boolean().default(false),
  /** Reached the final docs section. Sticky-true. */
  docsScrolledToEnd: z.boolean().default(false),
  /** Cumulative active time on the docs page (ms). Monotonic max, capped. */
  docsActiveMs: z.number().int().nonnegative().default(0),
  test: trainingTestSchema.default({}),
  certificate: trainingCertificateSchema.default({}),
  /** Admin-nudge bookkeeping (server/admin-written; client can't set it — the
   *  restricted patch shape below omits it). */
  nudge: trainingNudgeSchema.default({}),
  updatedAt: isoDateString.nullable().default(null),
});
export type TrainingState = z.infer<typeof trainingStateSchema>;

/**
 * The ONLY training fields a client may PATCH. `videoCoverageComplete`,
 * `test`, and `certificate` are server-derived and deliberately absent here.
 */
export const trainingProgressPatchSchema = z
  .object({
    videoWatchedPct: z.number().min(0).max(100).optional(),
    docsScrolledToEnd: z.literal(true).optional(),
    docsActiveMs: z.number().int().nonnegative().max(86_400_000).optional(),
  })
  .strict();
export type TrainingProgressPatch = z.infer<typeof trainingProgressPatchSchema>;

export const onboardingProgressSchema = z.object({
  /** First time the user landed on any `/metric-hub/*` route. Stamped
   *  server-side on the first `GET /metric-hub/onboarding-progress`
   *  call; never overwritten. `null` means "user has never opened the
   *  Hub before" — drives the first-visit redirect. */
  firstSeenAt: isoDateString.nullable().default(null),
  /** Selected on the goal-router. Drives which training track is
   *  highlighted + which tours appear in the catalog. `null` means
   *  the user hasn't chosen yet — Start Here shows the goal-router. */
  role: onboardingRoleSchema.nullable().default(null),
  /** Set of mastered skills. Order is irrelevant — UIs define the
   *  canonical display sequence per track. */
  skills: z.array(onboardingSkillSchema).default([]),
  /** State of the tour currently running (if any). `null` outside an
   *  active tour. */
  tourState: onboardingTourStateSchema.nullable().default(null),
  /** Per-tour completion record. Keyed by tour ID. */
  tourCompletions: z.record(onboardingTourIdSchema, tourCompletionSchema).default({}),
  /** Server-derived consecutive-day streak. */
  streak: onboardingStreakSchema.default({ count: 0, lastActionAt: null }),
  /** Server-derived badges. */
  badges: z.array(onboardingBadgeIdSchema).default([]),
  /** User clicked "I'm done" on the welcome banner. Hides the catalog
   *  banner globally — Start Here itself is still reachable. */
  dismissed: z.boolean().default(false),
  /** Training-certificate progress + the issued certificate. Defaults to a
   *  zeroed block so legacy docs parse transparently (no backfill needed). */
  training: trainingStateSchema.default({}),
  updatedAt: isoDateString,
});
export type OnboardingProgress = z.infer<typeof onboardingProgressSchema>;

/**
 * PATCH body — every field optional. The handler merges + bumps
 * `updatedAt`; the frontend never sets `updatedAt`, `streak`, or
 * `badges` directly (server is authoritative for those). The client
 * may still send them to be friendly, but the handler will overwrite.
 */
export const onboardingProgressPatchSchema = onboardingProgressSchema
  .partial()
  .omit({ updatedAt: true })
  // Replace the full `training` shape with the restricted client-settable one
  // so a client cannot PATCH test results or self-issue a certificate.
  .extend({ training: trainingProgressPatchSchema.optional() });
export type OnboardingProgressPatch = z.infer<typeof onboardingProgressPatchSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Derived training eligibility — computed server-side and returned alongside
// the progress doc on GET. Not persisted. `ownedAny` marks the user as an
// owner of ≥1 metric/model so the propose-skill checklist shows done.
// ──────────────────────────────────────────────────────────────────────────

export const trainingRequirementSchema = z.enum(['tour', 'video', 'docs', 'test']);
export type TrainingRequirement = z.infer<typeof trainingRequirementSchema>;

export const trainingEligibilitySchema = z.object({
  requirements: z.object({
    tour: z.boolean(),
    video: z.boolean(),
    docs: z.boolean(),
    test: z.boolean(),
  }),
  /** First three requirements met → the test section unlocks. */
  eligibleForTest: z.boolean(),
  /** All four met → certificate is (or will be) issued. */
  certified: z.boolean(),
  /** User owns ≥1 metric or semantic model (derived, not persisted). */
  ownedAny: z.boolean(),
});
export type TrainingEligibility = z.infer<typeof trainingEligibilitySchema>;

/** GET /metric-hub/onboarding-progress response = the doc + derived eligibility. */
export const onboardingProgressResponseSchema = onboardingProgressSchema.extend({
  eligibility: trainingEligibilitySchema,
});
export type OnboardingProgressResponse = z.infer<typeof onboardingProgressResponseSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Admin adoption dashboard — aggregates the per-user onboarding state above
// (read-only). Powers `GET /metric-hub/onboarding-adoption` (admin) and the
// `/admin/adoption` page. All UI copy that consumes this is English.
// ──────────────────────────────────────────────────────────────────────────

/** One user's onboarding snapshot for the admin adoption table. */
export const adoptionUserRowSchema = z.object({
  email: z.string(),
  displayName: z.string().nullable(),
  area: z.string().nullable(),
  /** Onboarding goal-router role (creator/reviewer/consumer), not the system role. */
  role: onboardingRoleSchema.nullable(),
  firstSeenAt: isoDateString.nullable(),
  lastActionAt: isoDateString.nullable(),
  skillCount: z.number().int().nonnegative(),
  tourCount: z.number().int().nonnegative(),
  streakCount: z.number().int().nonnegative(),
  badgeCount: z.number().int().nonnegative(),
  started: z.boolean(),
  pickedRole: z.boolean(),
  masteredAny: z.boolean(),
  completedTour: z.boolean(),
  // ── Training certificate (admin visibility) ──
  certificateStatus: z.enum(['certified', 'eligible', 'in_progress']),
  /** Share of the 4 requirements completed (0/25/50/75/100). */
  trainingProgressPct: z.number().int().min(0).max(100),
  /** Requirements still pending — drives the "what's missing" checklist. */
  missing: z.array(trainingRequirementSchema),
  certificateIssuedAt: isoDateString.nullable(),
  certificateSerial: z.string().nullable(),
  testBestScore: z.number().int().min(0).max(5),
  testAttempts: z.number().int().nonnegative(),
  /** Last time an admin reminded this user to finish training (dedupe + UI). */
  lastNudgedAt: isoDateString.nullable(),
});
export type AdoptionUserRow = z.infer<typeof adoptionUserRowSchema>;

export const adoptionSummarySchema = z.object({
  totalUsers: z.number().int().nonnegative(),
  started: z.number().int().nonnegative(),
  pickedRole: z.number().int().nonnegative(),
  masteredAny: z.number().int().nonnegative(),
  completedAnyTour: z.number().int().nonnegative(),
  activeLast7: z.number().int().nonnegative(),
  activeLast30: z.number().int().nonnegative(),
  byRole: z.object({
    creator: z.number().int().nonnegative(),
    reviewer: z.number().int().nonnegative(),
    consumer: z.number().int().nonnegative(),
    none: z.number().int().nonnegative(),
  }),
  avgSkills: z.number().nonnegative(),
  avgTours: z.number().nonnegative(),
  topBadges: z.array(z.object({ badge: onboardingBadgeIdSchema, count: z.number().int() })),
  /** Users who earned the training certificate. */
  certified: z.number().int().nonnegative(),
  /** Users who unlocked the test but haven't certified yet. */
  eligibleForTest: z.number().int().nonnegative(),
  /** Average training completion across all users (0..100). */
  avgTrainingPct: z.number().nonnegative(),
});
export type AdoptionSummary = z.infer<typeof adoptionSummarySchema>;

export const adoptionDashboardResponseSchema = z.object({
  generatedAt: isoDateString,
  summary: adoptionSummarySchema,
  users: z.array(adoptionUserRowSchema),
});
export type AdoptionDashboardResponse = z.infer<typeof adoptionDashboardResponseSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Admin "remind to finish training" — POST /metric-hub/training-reminders.
// ──────────────────────────────────────────────────────────────────────────

/** Bulk-capped at 50 per request to bound the per-call fan-out (enforced
 *  server-side regardless of what the UI sends). */
export const trainingReminderRequestSchema = z.object({
  emails: z.array(z.string().email()).min(1).max(50),
});
export type TrainingReminderRequest = z.infer<typeof trainingReminderRequestSchema>;

export const trainingReminderResultSchema = z.object({
  results: z.array(
    z.object({
      email: z.string(),
      sent: z.boolean(),
      reason: z.enum(['deduped', 'already_certified']).nullable(),
    }),
  ),
  sentCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
});
export type TrainingReminderResult = z.infer<typeof trainingReminderResultSchema>;
